import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const WhyChooseSwanScroll = () => {
    const sectionRef = useRef(null);
    const imageRef = useRef(null);
    const contentRef = useRef(null);
    const featuresRef = useRef([]);

    const features = [
        '1000+ Students Successfully Guided',
        '95% Visa Success Rate',
        'Expert & Experienced Counselors',
        'Specialized in New Zealand & Australia',
        'End-to-End Support (Admission to Visa)',
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Image parallax and morphing effect
            gsap.fromTo(
                imageRef.current,
                {
                    opacity: 0,
                    x: -100,
                    scale: 0.9,
                    rotateY: -10,
                },
                {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    rotateY: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );

            // Content slide in from right
            gsap.fromTo(
                contentRef.current,
                {
                    opacity: 0,
                    x: 100,
                },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );

            // Features staggered reveal with kinetic effect
            featuresRef.current.forEach((feature, index) => {
                gsap.fromTo(
                    feature,
                    {
                        opacity: 0,
                        x: 50,
                        scale: 0.9,
                    },
                    {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: feature,
                            start: 'top 90%',
                            end: 'top 60%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="about"
            className="py-20 bg-white relative overflow-hidden"
        >
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-red/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-red/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Image with parallax and morphing */}
                    <div ref={imageRef} className="relative">
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80"
                                alt="Students and counseling"
                                className="w-full h-auto transition-transform duration-700 hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        </div>
                        <div className="absolute -top-4 -left-4 w-full h-full border-4 border-primary-red rounded-2xl -z-10"></div>
                        {/* Floating badge */}
                        <div className="absolute -bottom-6 -right-6 bg-primary-red text-white p-6 rounded-xl text-center shadow-xl animate-float">
                            <span className="block text-3xl font-extrabold">95%</span>
                            <span className="text-sm">Visa Success Rate</span>
                        </div>
                    </div>

                    {/* Content with kinetic typography */}
                    <div ref={contentRef}>
                        <h2 className="text-4xl font-bold text-black mb-4">Why Choose Swan Global?</h2>
                        <p className="text-xl text-primary-red font-semibold mb-8">
                            Your Trusted Study Abroad Partner in Hyderabad
                        </p>
                        <ul className="space-y-4">
                            {features.map((feature, index) => (
                                <li
                                    key={index}
                                    ref={(el) => (featuresRef.current[index] = el)}
                                    className="flex items-center gap-4 pb-4 border-b border-lightGray group"
                                >
                                    <div className="w-10 h-10 bg-primary-red rounded-full flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                        <i className="fas fa-check"></i>
                                    </div>
                                    <span className="text-lg text-black font-medium">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyChooseSwanScroll;
